// Runtime X-ray lenses: a declarative spec compiled into the manifest the
// registry already reads.
//
// A committed lens is JavaScript — an `extract(turns)` and its spec module. A
// runtime lens cannot be, and the reason is not taste: the prod/staging CSP is
// `script-src 'self' 'wasm-unsafe-eval' …` with no `'unsafe-eval'`, so
// `new Function` and `eval` throw in the browser. Persisted JavaScript is
// therefore not an option for a lens a user mounts without a deploy, and the
// kernel store made the same call for the same reason: a typed step graph, never
// source.
//
// So the spec carries DATA and this module supplies the functions. That makes a
// runtime lens a strictly weaker class than a committed one — no reconstruct
// heuristics, no per-domain output provenance — and that is the tier boundary,
// not a defect. Runtime lenses are for sampling and iterating on a candidate; the
// ones that earn it get promoted to `xrays/<id>.xray.js` with real code.
//
// Two things here are load-bearing and easy to get silently wrong:
//
//   `op.key` MUST be the tool_use block id. The per-trace feed keys positions on
//   it AND derives its residual gauge from it (`claimed`), so a lens with a
//   different key double-renders: its ops appear in the feed AND its tool calls
//   still count as unclassified. Residual is the honest score for whether a lens
//   claimed anything, so getting this wrong makes a working lens look useless.
//
//   `RegExp` is not `eval`. A pattern arriving as a string is compiled with
//   `new RegExp`, which the CSP permits — it is not code execution. It is still
//   validated and length-capped here, because a spec can come from a model.

import { collectWindowOps, groupActivity } from "../../xray-factory/src/build/index.js";
import { buildStructure } from "../../xray-factory/src/build/structure.js";
import { toolResultText } from "./parseApiRequest.js";
import { validateXray } from "../../xray-factory/src/xray/manifest.js";
import { shellStatements, flagOf, positionalsOf } from "../../xray-factory/src/mine/statements.js";
import { SHELL_TOOL_NAMES, splitStatements, tokenize, resolveShellVars, stripAnsi } from "../../xray-factory/src/mine/shell.js";
import { skeletonizeShell } from "../../xray-factory/src/mine/skeleton.js";

// A pattern from a spec is untrusted input, not a developer's regex: cap it and
// reject the shapes that make a matcher pathological on a 400-tool window.
const MAX_PATTERN = 200;
const FIELD_RE = /^[a-z][a-zA-Z0-9_]{0,31}$/;
// Field sources. The two doors expose different things and the whitelist says so
// rather than letting a path walk anywhere: `input.*` / `result.*` are the tool
// door, `flag:` / `arg:` the shell door, and the rest are common to both.
const PATH_RE = new RegExp([
  "^(?:input|result)(?:\\.[A-Za-z0-9_$]+|\\[\\d+\\])*$",   // tool door
  "^(?:tool|command|program|output|outcome)$",               // either door
  "^flag:-{1,2}[A-Za-z0-9][\\w-]*$",                         // shell: a named flag's value
  "^arg:(?:\\d+|last|path)$",                                 // shell: a positional, the last one, or the last path-shaped one
].join("|"));

// `capture:<re>` over the command, `outputCapture:<re>` over what came back. Two
// prefixes rather than one with a source argument, because a regex may contain a
// colon and parsing it back out would be ambiguous.
//
// This is the source that makes an EMBEDDED SCRIPT legible. A `python3 -c` payload
// has no flags and no positionals — the values a reader wants are inside the source
// text — so without a capture the only bindable field is the whole command, which
// is exactly the thin reading a first mounted lens produced. A capture is also the
// only field source a model can propose from raw instances, which is what makes
// steering possible at all.
const CAPTURE_PREFIX = /^(capture|outputCapture):/;
const isCapture = (path) => CAPTURE_PREFIX.test(String(path || ""));

const lastSegment = (name) => String(name || "").split("__").pop();

// Where each shell-ish tool puts its program text. Same key order as the corpus
// builder — a tool carrying both takes the canonical one.
function shellCommandOf(input) {
  if (!input || typeof input !== "object") return null;
  for (const k of ["command", "cmd", "command_line", "script", "code"]) {
    const v = input[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
}

function safeRegExp(source) {
  if (typeof source !== "string" || !source || source.length > MAX_PATTERN) return null;
  try {
    return new RegExp(source, "i");
  } catch {
    return null;
  }
}

// Read `input.a.b` / `result.text` / `tool` off a call record. Returns undefined
// rather than throwing on a missing hop, because a spec written against one
// harness's argument names meets another's.
function readPath(call, path) {
  if (path === "tool") return call.tool;
  if (path === "program") return call.program;
  if (path === "output") return call.output;
  if (path === "outcome") return call.outcome;
  if (path === "command") return call.command ?? call.input?.command ?? call.input?.cmd ?? call.input?.script;
  // `flagOf` and `positionalsOf` are the shared primitives, not a local re-read:
  // getting flag-with-value wrong is the single most common defect in this
  // codebase — the first bare token after `kubectl -n prod` is the NAMESPACE, and
  // reading it as the verb silently dropped eleven of twelve calls.
  if (isCapture(path)) {
    const [, kind] = path.match(CAPTURE_PREFIX);
    const re = safeRegExp(path.slice(kind.length + 1));
    if (!re) return undefined;
    const subject = kind === "outputCapture" ? call.output : (call.command ?? call.input?.command);
    if (typeof subject !== "string" || !subject) return undefined;
    const m = re.exec(subject);
    // Group 1 when the pattern captures, else the whole match — so a spec can
    // locate a value without wrapping it, and a pattern with a group means "this
    // part of it".
    return m ? (m[1] ?? m[0]) : undefined;
  }
  if (path.startsWith("flag:")) return call.args ? flagOf(call.args, path.slice(5)) : undefined;
  if (path.startsWith("arg:")) {
    if (!call.args) return undefined;
    // `arg:last` is the thing most shell commands act ON — the file `sed` reads, the
    // path `grep` searches. Not always right, and a draft says so; but it is far more
    // discriminating than the program, which for a single-program cluster puts every
    // row in one group.
    if (path === "arg:last") {
      const pos = positionalsOf(call.args, call.valueFlags);
      return pos.length ? pos[pos.length - 1] : undefined;
    }
    // `arg:path` is `arg:last` with a shape test: the last positional that looks
    // like a filesystem path. Measured on a real corpus, the bare last
    // positional grouped a file-reading lens by test filters and subcommand
    // words — `cargo clippy --workspace` grouped under "clippy" — while the
    // path-shaped one lands on the file every time and falls back cleanly when
    // the command has none.
    if (path === "arg:path") {
      const pos = positionalsOf(call.args, call.valueFlags);
      for (let i = pos.length - 1; i >= 0; i -= 1) {
        const t = String(pos[i] || "");
        if (/[\\/]/.test(t) || /\.[A-Za-z0-9]{1,6}$/.test(t)) return t;
      }
      return pos.length ? pos[pos.length - 1] : undefined;
    }
    // valueFlags is NOT optional here. Without it `kubectl -n prod get pods` makes
    // `prod` the first positional and the verb vanishes — the defect this codebase
    // calls its most common, and one this lens re-created until a test caught it.
    return positionalsOf(call.args, call.valueFlags)[Number(path.slice(4))];
  }
  const parts = String(path).split(/\.|\[(\d+)\]/).filter((p) => p != null && p !== "");
  let cur = call;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return cur;
}

// `{channel}` → the op's channel field. Unresolved placeholders render as the
// literal field name rather than "undefined", so a mislabelled spec reads as a
// spec problem instead of as missing data.
function renderTemplate(tpl, op) {
  return String(tpl).replace(/\{([a-zA-Z0-9_]+)\}/g, (_, k) => {
    const v = op?.[k];
    return v == null || v === "" ? k : String(v);
  });
}


// The generic panel renders a fixed set of op shapes — `steps`, `checks`, `output`.
// A runtime lens's fields are none of those, which is why the first mounted lens
// showed timestamps and nothing else: it bound `program`/`command`/`outcome` and the
// panel had no name for any of them.
//
// So the compiler emits `op.fields`, an ordered label/value list the panel knows how
// to show. `output` additionally stays on `op.output` because the panel already has
// a better treatment for it (wrapped, line-capped) than a one-line field row.
function shapeOp(op, fields, order) {
  const rows = [];
  for (const name of order) {
    const v = op[name];
    if (v === undefined || v === "") continue;
    if (name === "output") { op.output = v; continue; }
    rows.push({ name, value: v });
  }
  if (rows.length) op.fields = rows;
  return op;
}

// ── SHAPES. Presets over the structural vocabulary below, reverse-engineered from
// the readings that work rather than invented. Two, because two is what the working
// panels actually distinguish:
//
//   tree    container → item, with a WHO rail and a census. What Research (host →
//           page) and Queries (target → entity) both are. Needs `group.within`.
//   stream  one level, ops in time order, the result rendered natively. The honest
//           shape when there is no container — a flat list is a real reading, and
//           dressing one up as a hierarchy invents a level.
//
// A third shape was considered for Recall (topic → consultation) and rejected: it
// IS `tree`, with a text-heavy item. Inventing a shape per panel is how a preset set
// becomes a panel-per-lens with extra steps.
const SHAPES = {
  tree: {
    census: [
      { label: "items", of: "inner" },
      { label: "containers", of: "outer" },
      { label: "runs", of: "ops" },
      { label: "endpoints", of: "endpoints" },
    ],
  },
  stream: {
    census: [
      { label: "targets", of: "outer" },
      { label: "runs", of: "ops" },
      { label: "sessions", of: "sessions" },
    ],
  },
};

export const SHAPE_NAMES = Object.keys(SHAPES);

/**
 * Validate a runtime spec's own shape, before it is compiled.
 *
 * Separate from `validateXray` because the two answer different questions: this
 * one asks whether the SPEC is well formed, that one whether the compiled lens
 * satisfies the host contract. A spec can be well formed and still fail the
 * contract (no tabReason on a trace-only lens), and the reverse is impossible —
 * so both run, in this order, and both sets of problems come back.
 */
export function validateRuntimeSpec(spec) {
  const problems = [];
  const s = spec && typeof spec === "object" ? spec : {};

  const tools = Array.isArray(s.match?.tools) ? s.match.tools.filter((t) => typeof t === "string" && t) : [];
  const pattern = s.match?.pattern;
  const programs = Array.isArray(s.match?.shell?.programs)
    ? s.match.shell.programs.filter((t) => typeof t === "string" && t) : [];
  const shellPattern = s.match?.shell?.pattern;
  const shellSkeleton = s.match?.shell?.skeleton;
  if (shellSkeleton !== undefined && (typeof shellSkeleton !== "string" || !shellSkeleton.trim())) {
    problems.push("match.shell.skeleton: must be a non-empty skeleton string");
  }
  // A cluster is one behaviour wearing SEVERAL exact shapes: the miner folds
  // near-duplicates (one flag apart) into a surviving skeleton, so a door that
  // tests only the survivor silently drops the absorbed variants — a "20×"
  // cluster can mount as a lens that matches eight. `skeletons` is the set of
  // shapes actually observed in the cluster's kept instances.
  const shellSkeletons = s.match?.shell?.skeletons;
  if (shellSkeletons !== undefined) {
    if (!Array.isArray(shellSkeletons) || !shellSkeletons.length
      || shellSkeletons.some((x) => typeof x !== "string" || !x.trim())) {
      problems.push("match.shell.skeletons: must be a non-empty array of skeleton strings");
    } else if (shellSkeletons.length > 12) {
      problems.push("match.shell.skeletons: more than 12 shapes is a program family, not a cluster — remine rather than widening the door");
    }
  }
  const anySkeletonDoor = Boolean(shellSkeleton) || (Array.isArray(shellSkeletons) && shellSkeletons.length > 0);
  // The skeleton door's DISCRIMINATOR: a command must match the shape AND this
  // pattern. It exists because a skeleton is syntax only — "conflict-marker
  // finding" over `grep -n <ARG> <PATH.md>` claims every documentation search
  // sharing the shape unless the pattern that carries the meaning rides along.
  // The OUTPUT door. Some behaviours are defined by what came BACK, not by the
  // shape that produced it — "the agent addressed a path from the wrong root"
  // is one command shape in a sed cluster, another in a grep cluster, and a
  // third in cargo, sharing only `No such file or directory` in the result.
  // Without this the whole outcome-defined class is inexpressible, so the miner
  // could surface it and the lens still could not claim it.
  const shellOutput = s.match?.shell?.outputPattern;
  if (shellOutput !== undefined && !safeRegExp(shellOutput)) {
    problems.push(`match.shell.outputPattern: not a valid regex, or longer than ${MAX_PATTERN} chars`);
  }
  const shellWhere = s.match?.shell?.where;
  if (shellWhere !== undefined) {
    if (!anySkeletonDoor) {
      problems.push("match.shell.where: narrows the skeleton door, so it needs match.shell.skeleton(s) beside it");
    } else if (!safeRegExp(shellWhere)) {
      problems.push(`match.shell.where: not a valid regex, or longer than ${MAX_PATTERN} chars`);
    }
  }
  if (!tools.length && !pattern && !programs.length && !shellPattern && !anySkeletonDoor && !shellOutput) {
    problems.push(
      "match: needs a door — `tools`/`pattern` for structured calls, or `shell.programs`/`shell.pattern` "
      + "for commands. The same act often arrives through BOTH, and mined separately each half falls under "
      + "threshold, so declare both when it does.",
    );
  }
  // The two SHELL doors are alternatives, not a pair. `skeleton` matches a whole
  // command once; `programs`/`pattern` match each statement inside it. Declaring both
  // ran both passes over the same command: `npx vitest run | tail -20` came out as
  // three ops — one for the pipeline and one per stage — and the two stage keys are
  // not `tool_use` block ids, which is the double-render the host's key contract
  // exists to prevent. Refused rather than silently preferring one, because a spec
  // whose declaration is quietly ignored teaches its author nothing.
  if (anySkeletonDoor && (programs.length || shellPattern)) {
    problems.push(
      "match.shell: `skeleton` and `programs`/`pattern` are alternative doors — a skeleton matches the "
      + "whole command, the others match each statement in it, and declaring both emits an op per stage "
      + "ON TOP of the one for the command. Keep the skeleton for a pipeline, the programs for a statement.",
    );
  }
  for (const [label, p] of [["match.pattern", pattern], ["match.shell.pattern", shellPattern]]) {
    if (p !== undefined && !safeRegExp(p)) {
      problems.push(`${label}: not a valid regex, or longer than ${MAX_PATTERN} chars`);
    }
  }

  // Shell-only sources on a spec with no shell door bind nothing, silently — the
  // op renders with a field missing and reads as the tool not carrying it.
  const shellDoor = programs.length || shellPattern || anySkeletonDoor || shellOutput;
  if (!shellDoor) {
    for (const [name, path] of Object.entries(s.fields || {})) {
      if (typeof path === "string" && (path.startsWith("flag:") || path.startsWith("arg:"))) {
        problems.push(`fields.${name}: \`${path}\` reads a shell statement, but this spec declares no shell door`);
      }
    }
  }

  // The lens's PROOF OBLIGATIONS: named probes for the semantics its title
  // claims, re-executed on every op it claims. Optional at the spec level — a
  // hand-written lens may be purely structural — but when present each probe
  // faces the same regex discipline as the doors, because a probe that cannot
  // run is a claim that cannot fail.
  if (s.checks !== undefined) {
    if (!Array.isArray(s.checks)) {
      problems.push("checks: must be an array of {name, on, pattern}");
    } else {
      if (s.checks.length > 6) problems.push("checks: more than 6 is a grammar — promote to a committed extractor");
      s.checks.forEach((c, i) => {
        if (!c || typeof c !== "object") { problems.push(`checks[${i}]: must be an object`); return; }
        const name = String(c.name || "").trim();
        if (!name || name.length > 48) problems.push(`checks[${i}].name: required, <=48 chars — it labels the probe on every op`);
        if (c.on !== "command" && c.on !== "output") problems.push(`checks[${i}].on: must be \`command\` or \`output\` — what the probe reads`);
        if (!safeRegExp(c.pattern)) problems.push(`checks[${i}].pattern: not a valid regex, or longer than ${MAX_PATTERN} chars`);
        if (c.required !== undefined && typeof c.required !== "boolean") {
          problems.push(`checks[${i}].required: optional, but must be a boolean — true turns the probe into a door`);
        }
      });
    }
  }

  const fields = s.fields && typeof s.fields === "object" ? s.fields : {};
  const names = Object.keys(fields);
  if (!names.length) problems.push("fields: at least one, so an op carries something a reader can read");
  for (const [name, path] of Object.entries(fields)) {
    if (!FIELD_RE.test(name)) problems.push(`fields.${name}: name must be lowerCamel, <=32 chars`);
    // Whitelisted roots only. A path is a reader into a call record, and letting
    // it walk anywhere invites a spec that reads the whole request body into an op.
    if (typeof path !== "string") {
      problems.push(`fields.${name}: path must be a string`);
    } else if (isCapture(path)) {
      const body = path.slice(path.indexOf(":") + 1);
      if (!body) problems.push(`fields.${name}: capture needs a pattern`);
      else if (!safeRegExp(body)) {
        problems.push(`fields.${name}: capture pattern is not a valid regex, or longer than ${MAX_PATTERN} chars`);
      }
    } else if (!PATH_RE.test(path)) {
      problems.push(
        `fields.${name}: path must be \`tool\` / \`command\` / \`program\` / \`output\` / \`outcome\`, `
        + "or start `input.` / `result.` / `flag:` / `arg:` / `capture:` / `outputCapture:`",
      );
    }
  }

  const by = s.group?.by;
  if (!by || !names.includes(by)) {
    problems.push("group.by: required, and must name one of `fields` — it is the unit a reader sees");
  }

  const within = s.group?.within;
  if (within !== undefined) {
    if (!names.includes(within)) {
      problems.push(`group.within: "${within}" must name one of \`fields\` — it is the item inside the container`);
    } else if (within === by) {
      // A container whose item is itself is one level wearing two, and it renders as
      // every row containing exactly one row.
      problems.push("group.within must differ from group.by, or the nesting has one level pretending to be two");
    }
  }

  if (s.shape !== undefined && !SHAPE_NAMES.includes(s.shape)) {
    problems.push(`shape: must be one of ${SHAPE_NAMES.join(", ")}`);
  }
  if (s.shape === "tree" && !within) {
    problems.push("shape: tree needs group.within — a tree with no item level is a stream");
  }

  const compare = s.sighting?.compare;
  if (compare !== undefined && !names.includes(compare)) {
    problems.push(`sighting.compare: "${compare}" must name one of \`fields\` — it is the observation tracked across sightings`);
  }

  const resultAs = s.result?.as;
  if (resultAs !== undefined && !["text", "table", "json"].includes(resultAs)) {
    problems.push("result.as: must be text, table or json");
  }
  if (s.result?.from !== undefined && !names.includes(s.result.from)) {
    problems.push(`result.from: "${s.result.from}" must name one of \`fields\``);
  }
  return { ok: problems.length === 0, problems };
}

/**
 * Compile a declarative spec into a manifest descriptor.
 *
 * @returns {{ok, problems: string[], manifest: object|null}}
 *   `manifest` is null unless BOTH the spec shape and the host contract passed —
 *   a half-valid lens must never reach the registry, where one bad manifest is
 *   reported but its valid siblings still mount.
 */
export function compileRuntimeLens(spec) {
  const shape = validateRuntimeSpec(spec);
  if (!shape.ok) return { ok: false, problems: shape.problems, manifest: null };

  const s = spec;
  const fields = Object.entries(s.fields);
  const fieldOrder = fields.map(([name]) => name);
  const within = s.group?.within || null;
  const compareField = s.sighting?.compare || null;
  // A spec that nests is a tree whether or not it said so; one that does not is a
  // stream. Declaring the shape only matters when it disagrees, and then the
  // validator has already refused the disagreement.
  const renderShape = s.shape || (within ? "tree" : "stream");
  const exact = new Set((s.match.tools || []).map((t) => lastSegment(t).toLowerCase()));
  const pattern = s.match.pattern ? safeRegExp(s.match.pattern) : null;
  const programs = new Set((s.match.shell?.programs || []).map((p) => p.toLowerCase()));
  // Which flags consume the next token. DERIVED from the spec's own `flag:` reads —
  // a spec that reads `flag:-n` has already told us `-n` takes a value, so making
  // the author declare it twice is an invitation to declare it once. Unioned with
  // an explicit list for value-flags the spec does not itself read but which sit
  // between the program and the positional it wants.
  const valueFlags = new Set([
    ...fields.filter(([, path]) => path.startsWith("flag:")).map(([, path]) => path.slice(5)),
    ...(Array.isArray(s.match.shell?.valueFlags) ? s.match.shell.valueFlags : []),
  ]);
  const shellPattern = s.match.shell?.pattern ? safeRegExp(s.match.shell.pattern) : null;
  // The SKELETON door matches a WHOLE COMMAND against the normalized shape it was
  // clustered as. It exists because the alternative shreds a pipeline twice over.
  //
  // `npx vitest … | grep -v noise | tail -20` is one act. Declared as
  // `programs: [npx, grep, tail]` it becomes a DISJUNCTION — every statement whose
  // program is any of those — so a lens for "read filtered test output" matched 91
  // unrelated greps. And because `splitStatements` splits on pipes, even the correct
  // instances arrive as three statements in three groups, scattering the behaviour
  // across the rows a reader sees.
  //
  // Matching the shape and emitting ONE op per command fixes both: exactly the
  // clustered pipelines, one row each.
  const wantSkeletons = new Set([
    ...(typeof s.match.shell?.skeleton === "string" ? [s.match.shell.skeleton.trim()] : []),
    ...(Array.isArray(s.match.shell?.skeletons) ? s.match.shell.skeletons.map((x) => String(x).trim()) : []),
  ].filter(Boolean));
  const wantSkeleton = wantSkeletons.size > 0;
  // The discriminator, compiled once. Only consulted by the skeleton pass — the
  // validator refuses `where` without a skeleton, so a null here means "no
  // narrowing", never "narrowing that failed to compile".
  const whereRe = wantSkeleton && s.match.shell?.where ? safeRegExp(s.match.shell.where) : null;
  const outputRe = s.match.shell?.outputPattern ? safeRegExp(s.match.shell.outputPattern) : null;
  const hasStatementDoor = programs.size > 0 || Boolean(shellPattern);
  const hasShellDoor = hasStatementDoor || Boolean(wantSkeleton) || Boolean(outputRe);

  // The proof obligations, compiled once. The door decides what the lens
  // CLAIMS; the checks decide whether each claimed op actually shows the
  // semantics the title asserts, and the verdict rides on the op (`✓/✗` per
  // probe) instead of living only in the judge's transcript. A probe marked
  // `required` joins the door — an editor's tool, never the draft's default,
  // because silently excluding a failing op hides the very finding the probe
  // exists to surface.
  const compiledChecks = (Array.isArray(s.checks) ? s.checks : []).map((c) => ({
    name: String(c.name || "").trim(),
    on: c.on,
    re: safeRegExp(c.pattern),
    required: c.required === true,
  }));
  const checkSubject = (call, on) => (on === "output"
    ? (call.output ?? call.result?.text)
    : (call.command ?? call.input?.command ?? call.input?.cmd ?? call.input?.script));
  const applyChecks = (op, call) => {
    if (!compiledChecks.length) return true;
    const results = compiledChecks.map((c) => {
      const subject = checkSubject(call, c.on);
      return { name: c.name, ok: Boolean(c.re) && typeof subject === "string" && c.re.test(subject) };
    });
    if (compiledChecks.some((c, i) => c.required && !results[i].ok)) return false;
    op.checks = results;
    return true;
  };

  // Does this TOOL CALL belong to the lens?
  const matchesTool = (name) => {
    const n = String(name || "");
    if (exact.size && exact.has(lastSegment(n).toLowerCase())) return true;
    return pattern ? pattern.test(n) : false;
  };

  // Does this shell STATEMENT belong to it? Matched on the resolved program, which
  // `shellStatements` has already dug out from behind assignments and wrappers — a
  // `docker exec … psql` is a database call whose leading token is docker, and
  // reading only the first token missed the dominant form of SQL in a real corpus.
  const matchesStatement = (st) => {
    if (programs.size && programs.has(String(st.program || "").toLowerCase())) return true;
    return shellPattern ? shellPattern.test(st.text) : false;
  };

  // The SUBSTRATE matcher is name-level, because that is what the window's tools
  // dimension keys on, and it is deliberately a superset: a shell-door lens has to
  // accept every shell tool name so its window is sampled at all, and the statement
  // matcher above stays the precision layer.
  const matches = (name) => {
    if (matchesTool(name)) return true;
    return hasShellDoor && SHELL_TOOL_NAMES.test(lastSegment(String(name || "")));
  };

  // The head of a pipeline is the act; the tail stages are formatting. So `program`,
  // `flag:` and `arg:` read the FIRST stage, while `command` and any capture see the
  // whole pipeline — `arg:last` over the whole thing would return tail's `-20`.
  const headStage = (command) => {
    for (const st of splitStatements(resolveShellVars(stripAnsi(String(command || ""))))) {
      const text = st.text.trim();
      if (!text || text.startsWith("#")) continue;
      const argv = tokenize(text).filter(Boolean);
      let i = 0;
      while (i < argv.length) {
        const t = argv[i];
        if (/^\$?\w+=/.test(t)) { i += 1; continue; }
        if (/^(?:sudo|env|command|nohup|time|exec|builtin)$/i.test(t)) { i += 1; continue; }
        if (/^(?:cd|pushd|source|\.)$/i.test(t)) { i += 2; continue; }
        break;
      }
      const raw = argv[i];
      if (!raw || !/^[A-Za-z_$@./\\][\w.@:+-]*$/.test(raw)) continue;
      return { program: raw.split(/[\\/]/).pop().replace(/\.exe$/i, ""), args: argv.slice(i + 1) };
    }
    return null;
  };

  // One request body → its ops. Walks tool_use blocks, pairs each with its
  // result, and binds the declared fields.
  const extract = (turns) => {
    const ops = [];
    // Every pass exits through here, so a claimed op cannot dodge its probes.
    // The verdicts render as one `claims` row — "✓ writes the script · ✗
    // reports counts" — which is the falsifier firing visibly on live traffic
    // instead of rotting in a stored verdict.
    const emit = (op, call) => {
      if (!applyChecks(op, call)) return;
      const shaped = shapeOp(op, fields, fieldOrder);
      if (shaped.checks) {
        shaped.fields = [...(shaped.fields || []), {
          name: "claims",
          value: shaped.checks.map((c) => `${c.ok ? "✓" : "✗"} ${c.name}`).join(" · "),
        }];
      }
      ops.push(shaped);
    };
    const results = new Map();
    for (const turn of turns || []) {
      for (const b of turn?.blocks || []) {
        if (b?.type === "tool_result" && b.tool_use_id) results.set(b.tool_use_id, b);
      }
    }
    for (const turn of turns || []) {
      for (const b of turn?.blocks || []) {
        if (b?.type !== "tool_use" || !matchesTool(b.name)) continue;
        const res = b.id ? results.get(b.id) : null;
        const call = {
          tool: b.name,
          input: b.input || {},
          result: {
            text: toolResultText(res?.content),
            isError: Boolean(res?.isError),
          },
        };
        // `key` is the block id: the feed's position map and its residual gauge
        // both key on it. See the header note.
        const op = { key: b.id, tool: b.name, outcome: res ? (res.isError ? "errored" : "answered") : "unrecorded" };
        let bound = 0;
        for (const [name, path] of fields) {
          const v = readPath(call, path);
          if (v === undefined || v === null || v === "") continue;
          op[name] = typeof v === "object" ? JSON.stringify(v).slice(0, 2000) : String(v).slice(0, 4000);
          bound += 1;
        }
        op.bound = bound;
        emit(op, call);
      }
    }

    // The shell door. One op per matching STATEMENT, not per Bash call: a compound
    // command performs several acts, and collapsing them attributed one statement's
    // reach to all of them. `shellStatements` keys multi-statement blocks
    // `<blockId>#<i>`, which the feed resolves back to the block for positioning —
    // so several statements in one call each get a row and the call is claimed once.
    // WHOLE-COMMAND pass: one op per matching pipeline, keyed on the block id.
    // Runs for a skeleton door AND for an output-only door — the latter claims
    // any shell call whose RESULT matches, which is how an outcome-defined
    // behaviour (a path miss, a rate limit, an auth expiry) spans shapes.
    if (wantSkeleton || outputRe) {
      const results = new Map();
      for (const turn of turns || []) {
        for (const b of turn?.blocks || []) {
          if (b?.type === "tool_result" && b.tool_use_id) results.set(b.tool_use_id, b);
        }
      }
      for (const turn of turns || []) {
        for (const b of turn?.blocks || []) {
          if (b?.type !== "tool_use") continue;
          if (!SHELL_TOOL_NAMES.test(lastSegment(String(b.name || "")))) continue;
          const command = shellCommandOf(b.input);
          if (!command) continue;
          // Exact shape match against the OBSERVED shapes, so the lens claims
          // precisely what was clustered — survivors and absorbed variants both.
          if (wantSkeleton && !wantSkeletons.has(skeletonizeShell(command).core)) continue;
          // And the discriminator on top of the shape: a subset claim's lens
          // must not stamp its name on every command that merely LOOKS like the
          // behaviour. Non-matching calls stay unclaimed residual, which is the
          // honest reading.
          if (whereRe && !whereRe.test(command)) continue;
          const res = b.id ? results.get(b.id) : null;
          const text = stripAnsi(toolResultText(res?.content));
          // An output door tests what came BACK. A call with no captured result
          // cannot satisfy it — absence of evidence is not a match, and letting
          // it through would claim every unrecorded call in the window.
          if (outputRe && !outputRe.test(text || "")) continue;
          const head = headStage(command) || { program: "", args: [] };
          const call = {
            tool: "shell",
            program: head.program,
            command,
            args: head.args,
            valueFlags,
            output: text ? text.slice(0, 4000) : null,
            outcome: !res ? "unrecorded" : res.isError ? "errored" : "answered",
          };
          const op = { key: b.id, tool: head.program, outcome: call.outcome };
          let bound = 0;
          for (const [name, path] of fields) {
            const v = readPath(call, path);
            if (v === undefined || v === null || v === "") continue;
            op[name] = typeof v === "object" ? JSON.stringify(v).slice(0, 2000) : String(v).slice(0, 4000);
            bound += 1;
          }
          op.bound = bound;
          emit(op, call);
        }
      }
    }

    // STATEMENT pass: only when a statement door was declared. A spec cannot carry
    // both doors — `validateRuntimeSpec` refuses that, which is what keeps this pass
    // from re-emitting every stage of a pipeline the whole-command pass already
    // claimed.
    if (hasStatementDoor) {
      for (const st of shellStatements(turns || [])) {
        if (!matchesStatement(st)) continue;
        const call = {
          tool: "shell",
          program: st.program,
          command: st.text,
          args: st.args,
          valueFlags,
          output: st.output,
          // `outputStatus` distinguishes real program output from the harness's own
          // words and from a shell error. Absence of evidence is not evidence: a run
          // whose output was backgrounded is not a run that produced nothing.
          outcome: st.outputStatus === "observed" ? "answered"
            : st.outputStatus === "none" ? "unrecorded"
            : st.outputStatus,
        };
        const op = { key: st.key, tool: st.program, outcome: call.outcome };
        let bound = 0;
        for (const [name, path] of fields) {
          const v = readPath(call, path);
          if (v === undefined || v === null || v === "") continue;
          op[name] = typeof v === "object" ? JSON.stringify(v).slice(0, 2000) : String(v).slice(0, 4000);
          bound += 1;
        }
        op.bound = bound;
        emit(op, call);
      }
    }
    return ops;
  };

  const groupKey = (op) => {
    const v = op[s.group.by];
    // A missing group value is one row saying so, NOT one row per op keyed on
    // something unique — several unidentified things collapsed into a single row
    // reads as one piece of work, and the inverse (a row each) reads as fifty
    // distinct ones. The value is the reader's handle; when it is absent, say so
    // once.
    return v == null || v === "" ? `${s.group.by} not captured` : String(v);
  };

  const informedness = (op) => (op.outcome === "answered" ? 10000 : 0) + (op.bound || 0) * 100
    + fields.reduce((n, [name]) => n + String(op[name] || "").length, 0);

  const manifest = {
    id: s.id,
    label: s.label,
    title: s.title,
    consumer: s.consumer,
    decision: s.decision,

    tab: s.tab === false ? false : true,
    ...(s.tab === false ? { tabReason: s.tabReason } : {}),
    substrate: { label: s.substrate?.label || s.label, match: matches },
    // A spec may deliberately join an EXISTING domain rather than mint its own —
    // a runtime lens over a tool the Files reading already groups belongs in
    // `files`, not beside it. The contract demands a reason whenever the domain
    // and the id differ, which is what stops a typo'd domain from silently
    // scattering a lens's ops into someone else's section.
    feed: s.feed === false
      ? false
      : {
        domain: s.feed?.domain || s.id,
        ...(s.feed?.domain && s.feed.domain !== s.id
          ? { reason: s.feed.reason || `joins the existing ${s.feed.domain} reading rather than opening a section beside it` }
          : {}),
      },

    extract,
    collect: (prompts) => collectWindowOps(prompts, { informedness }),
    group: (flat) => groupActivity(flat, {
      key: groupKey,
      label: (op) => (s.group.label ? renderTemplate(s.group.label, op) : groupKey(op)),
      seed: () => ({ outcomes: {}, labels: [] }),
      fold: (g, op) => {
        g.outcomes[op.outcome] = (g.outcomes[op.outcome] || 0) + 1;
        // A claimed op whose probes did not all hold. Counted per group so a
        // reader sees WHERE the lens's own claim is failing, not just that it
        // sometimes does.
        if (op.checks?.some((c) => !c.ok)) g.unproven = (g.unproven || 0) + 1;
      },
      finish: (g) => {
        const labels = [];
        const errs = g.outcomes.errored || 0;
        if (errs) labels.push(`${errs} errored`);
        if (g.unproven) labels.push(`claims unproven on ${g.unproven}`);
        if (labels.length) g.labels = labels;
      },
    }),
    groupBy: groupKey,
    ignore: ["bound", "fields", "checks"],

    // The structural reading, alongside the flat one. Emitted always: a `stream` is
    // a real structure (one level, a WHO rail, a census), so there is no spec that
    // gets the flat panel by omission — which is what left every runtime lens
    // rendering as a program tally.
    structure: (flat) => buildStructure(flat, {
      outer: groupKey,
      outerLabel: (op) => (s.group.label ? renderTemplate(s.group.label, op) : groupKey(op)),
      ...(within
        ? {
          inner: (op) => {
            const v = op[within];
            return v == null || v === "" ? `${within} not captured` : String(v);
          },
          innerLabel: (op) => (s.group.innerLabel ? renderTemplate(s.group.innerLabel, op) : String(op[within] ?? "")),
        }
        : {}),
      ...(compareField ? { compare: (op) => op[compareField] ?? null } : {}),
      census: Array.isArray(s.census) && s.census.length ? s.census : SHAPES[renderShape].census,
    }),
    // How the panel renders a payload. `text` is the default because prose is what a
    // shell command returns; a table is a claim about the shape of the output and
    // only a spec that has looked should make it.
    render: {
      shape: renderShape,
      result: { as: s.result?.as || "text", from: s.result?.from || (fieldOrder.includes("output") ? "output" : null) },
      within: within || null,
      // The outer identity too, so the panel can suppress it as a field row: it is
      // already the section header.
      outer: s.group.by,
      compare: compareField || null,
    },

    emptyNote: s.emptyNote || `No ${s.label} work in this window`,
    // A runtime lens cannot know WHY a payload is missing the way a hand-written
    // extractor can — it has no model of the tool. So it reports the honest thing:
    // which declared fields the call did not carry.
    explainMissing: (op) => {
      if (op.bound > 0) return null;
      const declared = fields.map(([n]) => n).join(", ");
      return `the call carried none of this lens's fields (${declared})`;
    },

    ...(s.lineage
      ? {
        lineage: {
          kind: s.lineage.kind,
          ref: (op) => String(op[s.lineage.ref] || ""),
          role: () => s.lineage.role || "consulted",
          text: (op) => String(op[s.lineage.text] || ""),
          label: (op) => (s.lineage.label ? renderTemplate(s.lineage.label, op) : groupKey(op)),
          // Absent means NOT citable, the safe default: a citation is the graph's
          // strongest claim and a runtime spec has not earned it. A ref that is a
          // bare word would manufacture derivation out of a coincidence.
          ...(s.lineage.citable === true ? { citable: true } : {}),
        },
      }
      : { lineageReason: s.lineageReason || "a runtime lens does not claim provenance edges until it is promoted" }),

    runtime: true,
  };

  const contract = validateXray(manifest);
  if (!contract.ok) return { ok: false, problems: contract.problems, manifest: null };
  return { ok: true, problems: [], manifest };
}

/**
 * Compile a list of specs, keeping the good ones.
 *
 * One malformed spec must not blank the rest — the same reason `createRegistry`
 * yields its valid lenses alongside its problems. Reserved ids are refused here
 * rather than at the registry so the message names the spec.
 */
export function compileRuntimeLenses(specs, { reserved = [] } = {}) {
  const taken = new Set(reserved);
  const manifests = [];
  const problems = [];
  for (const spec of specs || []) {
    const id = spec?.id || "<no id>";
    if (taken.has(id)) {
      problems.push(`${id}: id already belongs to a built-in lens — rename it`);
      continue;
    }
    const r = compileRuntimeLens(spec);
    if (!r.ok) { problems.push(...r.problems.map((p) => `${id}: ${p}`)); continue; }
    taken.add(id);
    manifests.push(r.manifest);
  }
  return { manifests, problems };
}
